import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Bug, Zap, FileBarChart, Settings, LogIn, LogOut, Factory, ClipboardList, LayoutDashboard, Shield, Languages, Image, ExternalLink, Eye, EyeOff, Sliders, Palette, Monitor, Users, PlusSquare, Cpu, KeyRound, CreditCard, AlertTriangle, Workflow } from 'lucide-react';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useUIPreferencesStore } from '@/core/stores/ui-preferences-store';
import { Button } from '@/core/components/ui/button';
import { appConfig } from '@/core/config';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/core/components/ui/dropdown-menu';

interface DebugRoute {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

/**
 * Debug Toolbar - Only visible when debugMode feature flag is enabled
 * Controlled by runtime config features.debugMode (production) or environment check (dev/qa)
 * Provides quick navigation between different pages/features
 */
export function DebugToolbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();
  const { selectedOperator, clearSelection } = useOperatorStore();
  const {
    isDebugToolbarVisible,
    toggleDebugToolbar
  } = useUIPreferencesStore();

  // Get version with fallback
  const { version, isFallback } = appConfig.getVersion();

  // Only show in development/qa mode AND when debugMode is enabled
  // Check runtime config first (takes precedence), then fall back to environment check
  const runtimeConfig = appConfig.getRuntimeConfig();
  const isDebugModeEnabled = runtimeConfig?.features?.debugMode ?? !appConfig.isProduction();

  if (!isDebugModeEnabled) {
    return null;
  }

  // If toolbar is hidden by user preference, show floating toggle button
  if (!isDebugToolbarVisible) {
    return (
      <button
        onClick={toggleDebugToolbar}
        className="fixed top-2 right-2 z-50 p-2 bg-gray-800/80 hover:bg-gray-800 text-white rounded shadow-lg transition-all"
        title="Show Debug Toolbar"
      >
        <Eye className="h-4 w-4" />
      </button>
    );
  }

  const routes: DebugRoute[] = [
    {
      path: '/ev-admin',
      label: 'EV Admin',
      icon: Shield,
      description: 'Admin navigation menu'
    },
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Operator dashboard overview'
    },
    {
      path: '/energy-management',
      label: 'Energy Management',
      icon: Zap,
      description: 'Real-time energy usage monitoring'
    },
    {
      path: '/kiosk-reports',
      label: 'Kiosk Reports',
      icon: FileBarChart,
      description: 'Kiosk session reports and analytics'
    },
    {
      path: '/sessions-report',
      label: 'Sessions Report',
      icon: ClipboardList,
      description: 'Charging sessions report'
    },
    {
      path: '/data-error',
      label: 'Data Error Report',
      icon: AlertTriangle,
      description: 'Irregular delta meter value anomalies'
    },
    {
      path: '/ocpi-admin',
      label: 'OCPI Admin',
      icon: Settings,
      description: 'OCPI connection management'
    },
    {
      path: '/manufacturers',
      label: 'Manufacturers',
      icon: Factory,
      description: 'Manufacturer management'
    },
    {
      path: '/communities',
      label: 'Communities',
      icon: Users,
      description: 'Community pricing profiles'
    },
    {
      path: '/machine-setup',
      label: 'Machine Setup',
      icon: PlusSquare,
      description: 'Create and validate EV machines'
    },
    {
      path: '/kiosk-flow-management',
      label: 'Kiosk Flow Management',
      icon: Workflow,
      description: 'Build and manage kiosk flows per operator'
    },
    {
      path: '/translations',
      label: 'Translations',
      icon: Languages,
      description: 'Translation management'
    },
    {
      path: '/assets',
      label: 'Assets',
      icon: Image,
      description: 'Asset management'
    },
    {
      path: '/api-tokens',
      label: 'API Tokens',
      icon: KeyRound,
      description: 'External API token management'
    },
    {
      path: '/billing',
      label: 'Billing Configuration',
      icon: CreditCard,
      description: 'Operator billing config and thresholds'
    },
    {
      path: '/device-customizer/kiosk',
      label: 'Kiosk Customizer',
      icon: Palette,
      description: 'Customize kiosk UI appearance'
    },
    {
      path: '/kiosk-preview-demo',
      label: 'Kiosk Preview Demo',
      icon: Monitor,
      description: 'Preview kiosk display in demo mode'
    },
    {
      path: '/popup-playground',
      label: 'Popup Playground',
      icon: ExternalLink,
      description: 'Test popup window functionality'
    },
    {
      path: '/operator/2002123712/assignEv',
      label: 'Assign EV',
      icon: Sliders,
      description: 'EV operator kiosk settings management'
    },
    {
      path: `/machines/${appConfig.getEnvironment() === 'prod' ? '485182134' : '1000403131'}/AssignKiosk`,
      label: 'Assign Kiosk',
      icon: Cpu,
      description: 'Assign a kiosk machine to a location'
    }
  ];

  const currentRoute = routes.find(route => route.path === location.pathname);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = () => {
    logout();
    clearSelection();
    navigate('/select-operator');
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gray-800/80 backdrop-blur-sm border-b border-gray-700/50 opacity-30 hover:opacity-100 transition-opacity duration-200">
      <div className="flex items-center justify-between px-3 py-1">
        {/* Left side - Current page indicator */}
        <div className="flex items-center space-x-1.5">
          <Bug className="h-3 w-3 text-gray-300" />
          <span className="text-xs font-medium text-gray-300">{appConfig.getEnvironment().toUpperCase()}</span>
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs text-gray-200">
            {currentRoute?.label || 'Unknown Page'}
          </span>
          {selectedOperator && (
            <>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-yellow-300 font-medium">
                {selectedOperator.name}
              </span>
            </>
          )}
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs text-gray-400 font-mono" title={isFallback ? 'Using git version (runtime config unavailable)' : 'Runtime version'}>
            Version: {version}{isFallback && ' *'}
          </span>
        </div>

        {/* Right side - Navigation dropdown and hide button */}
        <div className="flex items-center space-x-1">
          {/* Navigation Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:bg-gray-700 h-6 px-2"
              >
                <span className="text-xs">Nav</span>
                <ChevronDown className="h-2.5 w-2.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-[80vh] overflow-y-auto">
              {routes.map((route, index) => {
                const Icon = route.icon;
                const isActive = location.pathname === route.path;

                return (
                  <DropdownMenuItem
                    key={route.path}
                    onClick={() => handleNavigate(route.path)}
                    className={isActive ? 'bg-yellow-50' : ''}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    <div className="flex flex-col">
                      <span className="font-medium">{route.label}</span>
                      {route.description && (
                        <span className="text-xs text-gray-500">{route.description}</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogin}>
                <LogIn className="h-4 w-4 mr-2" />
                <div className="flex flex-col">
                  <span className="font-medium">Dev Login</span>
                  <span className="text-xs text-gray-500">Test different credentials</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                <div className="flex flex-col">
                  <span className="font-medium">Logout</span>
                  <span className="text-xs text-gray-500">Clear authentication</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Hide Toolbar Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleDebugToolbar}
            className="text-gray-300 hover:bg-gray-700 h-6 px-2"
            title="Hide Debug Toolbar"
          >
            <EyeOff className="h-2.5 w-2.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}